import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Descriptions,
  Drawer,
  FormInstance,
  Row,
  Spin,
  Table,
  message,
  Collapse,
  Badge,
  Card as AntdCard, } from "antd";
import { ColumnType, ColumnsType } from "antd/lib/table";
import { AxiosResponse } from "axios";
import moment from "moment";
import debounce from "lodash.debounce";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import entitlements from "../config/entitlements";
import {
  Changelog,
  FormFieldConfig,
  Price,
  Product,
  ProductTemplate,
  SelectOptionsInterface,
  Template,
  TiersEnum,
  TransactionFields,
  TransactionFieldsEnum,
} from "../helpers/shared-interfaces";
import {
  capitalizeFirstLetter,
  generateDisplayErrorMessage,
  handleValidationErrors,
  injectKey,
  mapAPIErrorToFormError,
  parseTransactionFields,
  request,
  responseHandler,
  tierTypes,
  showTotalPagination,
} from "../helpers/utilities";
import {
  ADD_TEMPLATE_TO_PRODUCT,
  CLOSE_PRICE,
  CREATE_EZHOME_PRODUCT_PRICE,
  DELETE_PRODUCT,
  GET_PRODUCT_EXTENDED,
  GET_PRODUCT_TRANSACTION_FIELD_CHANGE_LOGS,
  REMOVE_TEMPLATE_FROM_PRODUCT,
} from "../gql/products";
import { GET_TEMPLATES_PAGINATED } from "../gql/templates";
import { SYNC_PRICES } from "../gql/general";
import * as Sentry from "@sentry/browser";
import { DEBOUNCE_TIME, loanSecurityTypes, tabPaneStyle } from "../helpers/constants";

interface State {
  name: string;
  isLoading: boolean;
  hasChanged: boolean;
  id: string;
  q: string;
  page: number;
  isSearchingTemplates: boolean;
  showDialog: boolean;
  product: Product | null;
  showPriceCloseModal: boolean;
  isPriceCloseLoading: boolean;
  selectedPrice: Price | undefined;
  subproducts?: Product[];
  isSubproduct: boolean;
  subproductId?: string;
  mainProductId?: string;
  isCreatingEzHomeProductPrice: boolean;
  showCreateEzHomeProductPriceModal: boolean;
  priceDate: string;
  isLoanOrFixedIncome: boolean;
  interestPerAnnum: number;
  commissionFee: number;
  processingFee: number;
  insuranceFee: number;
  maxAmount: number;
  maxTenor: number;
  minAmount: number;
  maxBalancePercentageAccessible: number;
  lienAmountMultiplier: number;
  productTemplates: ProductTemplate[],
  showAddProductTemplateModal: boolean;
  showRemoveTemplateModal: boolean;
  templates: Template[];
  isAddingProductTemplate: boolean;
  isRemovingProductTemplate: boolean;
  removingTemplateId: string | null;
  templateIdToRemove: string | null;
  productTransactionFieldChangeLogs: Changelog[];
  showProductHistoryDrawer: boolean;
  isProductHistoryLoading: boolean;
}
interface RouteParams {
  id: string;
  subproductId: string;
}

export const productTemplateColumns = (
  onRequestRemove: (id: string) => void,
  isRemoving: boolean,
  removingId: string | null,
  readOnly = false,
) => [
  {
    title: "Template Name",
    dataIndex: "name",
    key: "name",
    render(_: string, record: ProductTemplate): JSX.Element {
      if (!record.template) return <span>-</span>;
      return (
        <NavLink to={`/communication-templates/${record.template.id}`}>
          {record.template.name}
        </NavLink>
      );
    },
  },
  {
    title: "Template To",
    dataIndex: "to",
    key: "to",
    render(text: string, record: ProductTemplate): JSX.Element {
      return <span>{capitalizeFirstLetter(text || "")}</span>;
    },
  },
  {
    title: "Template Medium",
    dataIndex: "medium",
    key: "medium",
    render(text: string, record: ProductTemplate): JSX.Element {
      return <span>{capitalizeFirstLetter(text || "")}</span>;
    },
  },
  ...(readOnly
    ? []
    : [
        {
          title: "Action",
          key: "action",
          align: "center" as const,
          render: (_: string, record: ProductTemplate): JSX.Element => (
            <Button
              size="small"
              danger
              loading={isRemoving && removingId === record.id}
              onClick={() => onRequestRemove(record.id)}
            >
              Remove
            </Button>
          ),
        },
      ]),
];

function subproductsColumns(id: string) {
  return [
    {
      title: "Product",
      dataIndex: "name",
      key: "Product",
      width: "50%",
      render(text: string, record: Product): JSX.Element {
        return (
          <NavLink
            to={`/products/${id}/subproducts/${record?.id}`}
            data-cy="test-product-details-link"
          >
            {text}
          </NavLink>
        );
      },
    },
    {
      title: "Category",
      dataIndex: "productCategory",
      key: "Category",
      width: "40%",
      render(text: string, record: Product): JSX.Element {
        return <span>{record.productCategory.name}</span>;
      },
    },
    {
      title: "Prospect",
      dataIndex: "opportunityProspect",
      key: "Prospect",
      width: "10%",
      render(text: string, record: Product): JSX.Element {
        return (
          <span>
            {record.opportunityProspect && record.opportunityProspect.name}
          </span>
        );
      },
    },
  ];
}

export function getColumns<T extends Price = Price>(
  toggleShowClosePriceModal: (price: Price | undefined) => void,
  readOnly = false,
): ColumnType<T>[] {
  return [
    {
      title: "Price Date",
      dataIndex: "priceDate",
      key: "priceDate",
      width: "25%",
      render(text: string, record: Price): JSX.Element {
        return (
          <span>
            {record.priceDate
              ? moment(record.priceDate, "YYYY-MM-DD HH:mm:ss").format(
                "DD/MM/YYYY",
              )
              : "N/A"}
          </span>
        );
      },
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      width: "25%",
      render(text: string, record: Price): JSX.Element {
        return <span>{record.amount}</span>;
      },
    },
    {
      title: "Sync Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "25%",
      render(text: string, record: Price): JSX.Element {
        return <span>{moment(record.createdAt).format("llll")}</span>;
      },
    },
    {
      title: "Open",
      dataIndex: "isOpen",
      key: "price",
      width: "25%",
      render(text: string, record: Price): JSX.Element {
        if (readOnly) return <>{text ? "Open" : "Closed"}</>;
        return text ? (
          <Button
            onClick={() => toggleShowClosePriceModal(record)}
            className={"warning-btn"}
            id={record.id}
            key={1}
            style={{ marginRight: "5px" }}
          >
            Close Price
          </Button>
        ) : (
          <>Closed</>
        );
      },
    },
  ];
}

export function PriceHistoryTableFactory(
  data: Price[],
  columns: ColumnsType<Price>,
): JSX.Element {
  return (
    <Table
      bordered={true}
      columns={columns}
      dataSource={injectKey<Price>(data, "id")}
    />
  );
}

export const ClosePriceUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      id: "Redeemed Units",
      name: "redeemedUnits",
      required: true,
      inputType: "number",
      rule: "number",
      label: "Add Redeemed Units",
      placeholder: "Redeemed Units",
      colConfig: {
        span: 24,
      },
    },
  ];
  return (
    <>
      <Spin spinning={false}>
        <Row>
          <Col
            span={24}
            style={{
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                marginTop: "10px",
              }}
            >
              <BaseForm
                layout="vertical"
                reference={props.reference}
                fieldsToHide={[]}
                fields={fields}
              ></BaseForm>
            </div>
          </Col>
        </Row>
      </Spin>
    </>
  );
};

export const CreateEzHomeProductPriceUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
  isCreatingEzHomeProductPrice: boolean;
  dateChangeHandler: () => void;
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      id: "priceDate",
      name: "priceDate",
      rule: "date",
      inputType: "defaultDate",
      label: "Select Price Date",
      required: true,
      dateChangeHandler: props.dateChangeHandler,
      placeholder: "Select Price Date",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 24,
      },
    },
    {
      id: "price",
      name: "price",
      required: true,
      inputType: "number",
      rule: "number",
      label: "Price",
      placeholder: "Price",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 24,
      },
    },
  ];

  return (
    <Spin spinning={props.isCreatingEzHomeProductPrice}>
      <BaseForm
        layout="vertical"
        reference={props.reference}
        fieldsToHide={[]}
        fields={fields}
      />
    </Spin>
  );
};

export default class ViewProduct extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  closePriceReference: RefObject<FormInstance>;
  createEzHomeProductPriceReference: RefObject<FormInstance>;
  addProductTemplateReference: RefObject<FormInstance>;

  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      product: null,
      isLoading: true,
      id: "",
      name: "",
      page: 1,
      hasChanged: false,
      showDialog: false,
      showPriceCloseModal: false,
      isPriceCloseLoading: false,
      selectedPrice: undefined,
      isSubproduct: false,
      isCreatingEzHomeProductPrice: false,
      showCreateEzHomeProductPriceModal: false,
      priceDate: "",
      isLoanOrFixedIncome: false,
      interestPerAnnum: 0,
      commissionFee: 0,
      processingFee: 0,
      insuranceFee: 0,
      maxAmount: 0,
      maxTenor: 0,
      minAmount: 0,
      maxBalancePercentageAccessible: 0,
      lienAmountMultiplier: 0,
      productTemplates: [],
      showAddProductTemplateModal: false,
      showRemoveTemplateModal: false,
      templates: [],
      q: "",
      isSearchingTemplates: false,
      isAddingProductTemplate: false,
      isRemovingProductTemplate: false,
      removingTemplateId: null,
      templateIdToRemove: null,
      productTransactionFieldChangeLogs: [],
      showProductHistoryDrawer: false,
      isProductHistoryLoading: false,
    };
    this.deleteProduct = this.deleteProduct.bind(this);
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
    this.getProductData = this.getProductData.bind(this);
    this.showTier = this.showTier.bind(this);
    this.closePriceReference = React.createRef<FormInstance>();
    this.createEzHomeProductPriceReference = React.createRef<FormInstance>();
    this.addProductTemplateReference = React.createRef<FormInstance>();
  }

  toggleDeleteConfirmationModal(): void {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  }

  toggleShowClosePriceModal = (price: Price | undefined): void => {
    this.setState(
      {
        selectedPrice: price,
      },
      () => {
        this.setState((previousState: Readonly<State>) => ({
          showPriceCloseModal: !previousState.showPriceCloseModal,
        }));
      },
    );
  };

  redirectToPage = (path: string): void => {
    this.props.history.push(path);
  };

  getProductData(): void {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_PRODUCT_EXTENDED,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          const productCategoryName = data.data.product.productCategory.name;
          const value = this.isLoanOrFixedIncomeProvider(productCategoryName);
          if (!data.errors) {
            this.setState({
              product: data.data.product,
              subproducts: data.data.product?.subProducts,
              isLoanOrFixedIncome: value,
              interestPerAnnum: data.data.product.transactionFields?.interest_per_annum,
              commissionFee: data.data.product.transactionFields?.commission_fee,
              insuranceFee: data.data.product.transactionFields?.insurance_fee,
              maxAmount: data.data.product.transactionFields?.max_amount,
              maxTenor: data.data.product.transactionFields?.max_tenor,
              minAmount: data.data.product.transactionFields?.min_amount,
              maxBalancePercentageAccessible:
                data.data.product.transactionFields
                  ?.max_balance_percentage_accessible,
              lienAmountMultiplier:
                data.data.product.transactionFields?.lien_amount_multiplier,
              processingFee: data.data.product.transactionFields?.processing_fee,
            });
          } else {
            message.error("Failed to Get product data");
          }
        });
      },
    );
  }

  getTemplates(): void {
    this.setState({ isSearchingTemplates: true });
    request({
      query: GET_TEMPLATES_PAGINATED,
      variables: { page: 1, q: this.state.q || undefined },
    })
      .then(({ data }: AxiosResponse<any>): void => {
        if (!data.errors) {
          this.setState({
            templates: data.data.templates || [],
            isSearchingTemplates: false,
          });
        } else {
          this.setState({ isSearchingTemplates: false });
        }
      })
      .catch(() => {
        this.setState({ isSearchingTemplates: false });
      });
  }

  triggerDelayedSearch = debounce(() => {
    this.setState({ page: 1 }, this.getTemplates);
  }, DEBOUNCE_TIME);

  filterTemplates = (value: string): void => {
    this.setState(
      {
        q: value,
        isSearchingTemplates: true,
      },
      this.triggerDelayedSearch,
    );
  };

  deleteProduct(): void {
    const { id, isSubproduct, mainProductId } = this.state;

    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: DELETE_PRODUCT,
          variables: { id },
        })
          .then(({ data }: AxiosResponse<any>): void => {
            this.setState({ isLoading: false, showDialog: false });
            if (!data.errors && data.data.deleteProduct.result) {
              message.success("Product deleted successfuly");
              isSubproduct
                ? this.redirectToPage(`/products/${mainProductId}`)
                : this.redirectToPage("/products");
            } else {
              message.error("Failed to delete product");
            }
          })
          .catch((): void => {
            this.setState({ isLoading: false, showDialog: false });
          });
      },
    );
  }

  showTier(tier: string): string {
    if (tier === TiersEnum.tier2) {
      return tierTypes.tier_2;
    } else if (tier === TiersEnum.tier3) {
      return tierTypes.tier_3;
    } else {
      return tierTypes.tier_4;
    }
  }

  getTransactionFields(
    transactionFields: TransactionFields | TransactionFields[] | string | null,
  ): string[] {
    return Object.values(parseTransactionFields(transactionFields));
  }

  closePrice = async () => {
    const { selectedPrice } = this.state;

    this.setState({ isLoading: true });

    interface Response {
      data: {
        closePrice: {
          price: Price;
        };
      };
    }

    if (!selectedPrice) return;

    if (!this.closePriceReference.current) return;

    let data;

    try {
      data = await this.closePriceReference.current.validateFields([
        "redeemedUnits",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    request({
      query: CLOSE_PRICE,
      variables: {
        priceId: selectedPrice.id,
        redeemedUnits: data.redeemedUnits,
      },
    })
      .then(({ data: { data } }: AxiosResponse<Response>): void => {
        if (data.closePrice.price) {
          message.success("Price closed successfully");
          this.closePriceReference.current?.resetFields();
        }
        this.toggleShowClosePriceModal(undefined);
      })
      .catch((error) => {
        message.error("Error closing price");
      })
      .finally(() => {
        this.setState(
          {
            isLoading: false,
          },
          this.getProductData,
        );
      });
  };

  syncPrices = async (): Promise<void> => {
    const { product } = this.state;
    interface Response {
      data: {
        syncPricesManually: {
          result: boolean;
        };
      };
    }

    this.setState({
      isLoading: true,
    });

    try {
      const response: AxiosResponse<Response> = await request(
        {
          query: SYNC_PRICES,
          variables: { productId: product?.id },
        },
        { timeout: 30000 },
      );

      const { err } = responseHandler<Response>(response, "syncPricesManually");
      if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else {
        const result: boolean = response.data.data.syncPricesManually.result;
        result
          ? message.success("Prices synced sucessfully")
          : message.error(`Failed to sync prices - ${generateDisplayErrorMessage(err)}`);
      }
      this.setState({
        isLoading: false,
      });
    } catch (error) {
      if (error.code === "ECONNABORTED") {
        message.error(`Price Sync In Progress - ${generateDisplayErrorMessage(error)}`);
      } else {
        message.error(`Failed to sync prices - ${generateDisplayErrorMessage(error)}`);
      }
      Sentry.captureException(error);
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  };

  createEzHomeProductPrice = async () => {
    const { product } = this.state;
    interface Response {
      errors: [];
      price: Price;
    }

    if (!this.createEzHomeProductPriceReference.current) return;

    let data;

    this.setState({ isCreatingEzHomeProductPrice: true });

    try {
      data =
        await this.createEzHomeProductPriceReference.current.validateFields([
          "priceDate",
          "price",
        ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_EZHOME_PRODUCT_PRICE,
        variables: {
          ...data,
          productId: product?.id,
        },
      });

      const { mutation, err } = responseHandler<Response>(
        response,
        "createEzHomeProductPrice",
      );

      if (mutation.price && !err) {
        message.success("EzHome Product Price created successfully");
        this.getProductData();
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.createEzHomeProductPriceReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
      } else {
        message.error(`An Error Occurred. Your request couldn't be processed. - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      message.error(`Failed to create price - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isCreatingEzHomeProductPrice: false });
      this.toggleCreateEzHomeProductPriceModal();
    }
  };

  toggleCreateEzHomeProductPriceModal = () => {
    this.setState((prevState) => ({
      showCreateEzHomeProductPriceModal:
        !prevState.showCreateEzHomeProductPriceModal,
    }));
  };

  toggleAddProductTemplateModal = () => {
    this.setState(
      (prevState) => ({
        showAddProductTemplateModal: !prevState.showAddProductTemplateModal,
      }),
      () => {
        if (this.state.showAddProductTemplateModal) {
          this.getTemplates();
        }
      },
    );
  };

  addProductTemplateToProduct = async (): Promise<void> => {
    const { product } = this.state;
    if (!this.addProductTemplateReference.current) return;

    try {
      const data =
        await this.addProductTemplateReference.current.validateFields([
          "templateId",
          "templateTo",
          "templateMedium",
        ]);

      this.setState({ isAddingProductTemplate: true });

      const response: AxiosResponse<any> = await request({
        query: ADD_TEMPLATE_TO_PRODUCT,
        variables: {
          productId: product?.id,
          templateId: data.templateId,
          medium: data.templateMedium,
          to: data.templateTo,
        },
      });

      const { mutation, err } = responseHandler<any>(
        response,
        "addTemplateToProduct",
      );

      if (mutation.productTemplate && !err) {
        message.success("Template added to product");
        this.addProductTemplateReference.current.resetFields();
        this.toggleAddProductTemplateModal();
        this.getProductData();
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.addProductTemplateReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
      } else {
        message.error(`An Error Occurred. Your request couldn't be processed. - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      handleValidationErrors(error);
    } finally {
      this.setState({ isAddingProductTemplate: false });
    }
  };

  removeTemplateFromProduct = async (id: string): Promise<void> => {
    this.setState({ isRemovingProductTemplate: true, removingTemplateId: id });
    try {
      const response: AxiosResponse<any> = await request({
        query: REMOVE_TEMPLATE_FROM_PRODUCT,
        variables: { id },
      });

      const { mutation, err } = responseHandler<any>(
        response,
        "removeTemplateFromProduct",
      );

      if (mutation.result && !err) {
        message.success("Template removed from product");
        this.getProductData();
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else {
        message.error(`An Error Occurred. Your request couldn't be processed. - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      message.error(`Failed to remove template from product - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({
        isRemovingProductTemplate: false,
        removingTemplateId: null,
      });
    }
  };

  openRemoveTemplateModal = (id: string): void => {
    this.setState({ showRemoveTemplateModal: true, templateIdToRemove: id });
  };

  closeRemoveTemplateModal = (): void => {
    this.setState({ showRemoveTemplateModal: false, templateIdToRemove: null });
  };

  confirmRemoveTemplate = (): void => {
    const { templateIdToRemove } = this.state;
    if (!templateIdToRemove) return;
    this.closeRemoveTemplateModal();
    this.removeTemplateFromProduct(templateIdToRemove);
  };

  priceDateChangeHandler = async (): Promise<void> => {
    this.setState({
      priceDate:
        this.createEzHomeProductPriceReference.current?.getFieldValue(
          "priceDate",
        )._d,
    });
  };

  isLoanOrFixedIncomeProvider = (providerName?: string): boolean => {
    const name = (providerName || "").trim().toLowerCase();
    return name === "loans" || name === "fixed income";
  };

  isLoanProvider = (providerName?: string): boolean => {
    return (providerName || "").trim().toLowerCase() === "loans";
  };

  isAllowedLoanProductHistoryAudit = (auditItem?: string): boolean => {
    const normalizedAuditItem = (auditItem || "").toLowerCase();

    const allowedAuditTokens = [
      "interest rate",
      "interest per annum",
      "commission fee",
      "processing fee",
      "insurance fee",
      "maximum amount",
      "max amount",
      "minimum amount",
      "min amount",
      "maximum tenor",
      "max tenor",
      "max balance percentage accessible",
      "max balance % accessible",
      "lien amount multiplier",
    ];

    return allowedAuditTokens.some((token) =>
      normalizedAuditItem.includes(token),
    );
  };

  getProductTransactionFieldChangeLogs = async (): Promise<void> => {
    const { id } = this.state;
    if (!id) return;

    interface Response {
      productTransactionFieldChangeLogs: Changelog[];
    }

    this.setState({ isProductHistoryLoading: true });

    try {
      const { data }: AxiosResponse<{ data: Response }> = await request({
        query: GET_PRODUCT_TRANSACTION_FIELD_CHANGE_LOGS,
        variables: { productId: id },
      });

      this.setState({
        productTransactionFieldChangeLogs:
          data?.data?.productTransactionFieldChangeLogs || [],
      });
    } catch (error) {
      message.error("Failed to fetch product history");
      Sentry.captureException(error);
    } finally {
      this.setState({ isProductHistoryLoading: false });
    }
  };

  toggleProductHistoryDrawer = (): void => {
    this.setState(
      (prevState) => ({
        showProductHistoryDrawer: !prevState.showProductHistoryDrawer,
      }),
      () => {
        if (this.state.showProductHistoryDrawer) {
          this.getProductTransactionFieldChangeLogs();
        }
      },
    );
  };

  normalizeRateLabel = (auditItem?: string): string => {
    const normalizedAuditItem = (auditItem || "").toLowerCase();

    const rateLabelMap: { token: string; label: string }[] = [
      { token: "interest per annum", label: "Interest Per Annum %" },
      { token: "interest rate", label: "Interest Rate %" },
      { token: "commission fee", label: "Commission Fee" },
      { token: "processing fee", label: "Processing Fee %" },
      { token: "insurance fee", label: "Insurance Fee" },
      { token: "maximum amount", label: "Maximum Amount" },
      { token: "max amount", label: "Maximum Amount" },
      { token: "minimum amount", label: "Minimum Amount" },
      { token: "min amount", label: "Minimum Amount" },
      { token: "maximum tenor", label: "Maximum Tenor (months)" },
      { token: "max tenor", label: "Maximum Tenor (months)" },
      {
        token: "max balance percentage accessible",
        label: "Max Balance % Accessible",
      },
      { token: "max balance % accessible", label: "Max Balance % Accessible" },
      { token: "lien amount multiplier", label: "Lien Amount Multiplier" },
    ];

    const match = rateLabelMap.find(({ token }) =>
      normalizedAuditItem.includes(token),
    );

    return match?.label || auditItem || "N/A";
  };

  productHistoryColumns = (): ColumnsType<Changelog> => {
    return [
      {
        title: "Date",
        dataIndex: "createdAt",
        key: "createdAt",
        width: "25%",
        render: (text: string): JSX.Element => <span>{text || "N/A"}</span>,
      },
      {
        title: "User",
        dataIndex: "user",
        key: "user",
        width: "20%",
        render: (text: string): JSX.Element => <span>{text || "N/A"}</span>,
      },
      {
        title: "Rate",
        dataIndex: "auditItem",
        key: "auditItem",
        width: "25%",
        render: (text: string): JSX.Element => (
          <span>{this.normalizeRateLabel(text)}</span>
        ),
      },
      {
        title: "From",
        dataIndex: "oldValue",
        key: "oldValue",
        width: "15%",
        render: (text: string): JSX.Element => <span>{text || "N/A"}</span>,
      },
      {
        title: "To",
        dataIndex: "newValue",
        key: "newValue",
        width: "15%",
        render: (text: string): JSX.Element => <span>{text || "N/A"}</span>,
      },
    ];
  };

  componentDidMount(): void {
    const { id, subproductId } = this.props.match.params;
    const isSubproduct = !!subproductId;
    const productIdToUse = isSubproduct ? subproductId : id;

    this.setState(
      { id: productIdToUse, mainProductId: id, isSubproduct, subproductId },
      (): void => {
        this.getProductData();
      },
    );
  }

  render(): JSX.Element | null {
    const {
      isLoading,
      id,
      showDialog,
      product,
      subproducts,
      isSubproduct,
      subproductId,
      mainProductId,
      isCreatingEzHomeProductPrice,
      showCreateEzHomeProductPriceModal,
      isLoanOrFixedIncome,
      interestPerAnnum,
      insuranceFee,
      commissionFee,
      maxAmount,
      minAmount,
      maxTenor,
      maxBalancePercentageAccessible,
      lienAmountMultiplier,
      processingFee,
      showAddProductTemplateModal,
      showRemoveTemplateModal,
      templates,
      isAddingProductTemplate,
      isSearchingTemplates,
      isRemovingProductTemplate,
      removingTemplateId,
      productTransactionFieldChangeLogs,
      showProductHistoryDrawer,
      isProductHistoryLoading,
    } = this.state;
     


    const templateOptions: SelectOptionsInterface[] = templates.map(
      (template) => ({
        id: template.id,
        name: template.name,
        value: template.id,
      }),
    );

    const addProductTemplateFields: FormFieldConfig[] = [
      {
        id: "templateId",
        name: "templateId",
        inputType: "search",
        rule: "string",
        label: "Template",
        placeholder: "Select Template",
        required: true,
        changeHandler: this.filterTemplates,
        selectOptions: templateOptions,
        allowClear: true,
        clearHandler: () => this.filterTemplates(""),
        isSearching: isSearchingTemplates,
        colConfig: { span: 24 },
      },
      {
        id: "templateTo",
        name: "templateTo",
        inputType: "select",
        rule: "string",
        label: "Template To",
        placeholder: "Enter Template To",
        required: true,
        selectOptions: [
          { name: "Company", id: "company" },
          { name: "Customer", id: "customer" },
        ],
        allowClear: true,
        colConfig: { span: 24 },
      },
      {
        id: "templateMedium",
        name: "templateMedium",
        inputType: "select",
        rule: "string",
        label: "Template Medium",
        placeholder: "Enter Template Medium",
        required: true,
        selectOptions: [
          { name: "Attachment", id: "attachment" },
          { name: "Email", id: "email" },
          { name: "SMS", id: "sms" },
        ],

        allowClear: true,
        colConfig: { span: 24 },
      },
    ];

    const isPhasedOut = product?.isActive === false;

    const templateColumns = productTemplateColumns(
      this.openRemoveTemplateModal,
      isRemovingProductTemplate,
      removingTemplateId,
      isPhasedOut,
    );

    const transactionFields = this.getTransactionFields(
      product && product?.transactionFields,
    );
    const isLoanProvider = this.isLoanProvider(product?.productCategory?.name);

    const loanSecurityTypeValue = product?.transactionFields?.loan_security_type;
    const loanSecurityTypeLabel =
      loanSecurityTypes.find((type) => type.id === loanSecurityTypeValue)?.name ??
      loanSecurityTypeValue;

    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            <div>
              <ModalCreator
                showModal={showDialog}
                onOkay={this.deleteProduct}
                toggleModal={this.toggleDeleteConfirmationModal}
                inscription="Are you sure you want to phase out this product? It stays visible and keeps its history, but is closed to new activity."
                title="Confirm Phase Out"
              />
              <ModalCreator
                showModal={showRemoveTemplateModal}
                onOkay={this.confirmRemoveTemplate}
                toggleModal={this.closeRemoveTemplateModal}
                inscription="Remove this template from product?"
                title="Remove Template"
                okText="Remove"
                isLoading={isRemovingProductTemplate}
              />
              <Drawer
                title="Product Rates History"
                placement="right"
                width={900}
                visible={showProductHistoryDrawer}
                onClose={this.toggleProductHistoryDrawer}
              >
                <Spin spinning={isProductHistoryLoading}>
                  <Table
                    bordered={true}
                    pagination={false}
                    columns={this.productHistoryColumns()}
                    dataSource={productTransactionFieldChangeLogs
                      .filter((log) =>
                        this.isAllowedLoanProductHistoryAudit(log.auditItem),
                      )
                      .map((log, index) => ({
                        ...log,
                        key: `product-history-${index}`,
                      }))}
                    locale={{ emptyText: "No transaction field changes yet" }}
                  />
                </Spin>
              </Drawer>
              {ModalCreator({
                title: "Close Price",
                okType: "primary",
                okText: "Close Price",
                showModal: this.state.showPriceCloseModal,
                isLoading: this.state.isPriceCloseLoading,
                onOkay: this.closePrice,
                toggleModal: () => this.toggleShowClosePriceModal(undefined),
                inscription: ClosePriceUI({
                  reference: this.closePriceReference,
                }),
              })}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "20px",
                }}
              >
                {!isPhasedOut && product?.fundCode?.startsWith("EZH") && (
                  <EntitledSection
                    entitlement={entitlements.Products.createEzHomeProductPrice}
                  >
                    <Button
                      size="large"
                      onClick={this.toggleCreateEzHomeProductPriceModal}
                      data-cy="test-create-ezhome-product-price-button"
                      style={{
                        backgroundColor: "rgb(29, 55, 100)",
                        color: "white",
                        marginRight: "0px",
                        borderRadius: "4px",
                      }}
                    >
                      <PlusOutlined />
                      Create Product Price
                    </Button>
                  </EntitledSection>
                )}
                {isLoanProvider && (
                  <Button
                    className="base-button"
                    size="large"
                    onClick={this.toggleProductHistoryDrawer}
                    style={{
                      backgroundColor: "rgb(29, 55, 100)",
                      color: "white",
                      marginRight: "0px",
                      marginLeft: "10px",
                      borderRadius: "4px",
                    }}
                  >
                    Product History
                  </Button>
                )}
                {!isPhasedOut && (
                  <EntitledSection entitlement={entitlements.Products.edit}>
                    <Button
                      className="base-button"
                      size="large"
                      onClick={this.syncPrices}
                      data-cy="test-new-product-button"
                      style={{
                        backgroundColor: "rgb(29, 55, 100)",
                        color: "white",
                        marginRight: "0px",
                        marginLeft: "10px",
                        borderRadius: "4px",
                      }}
                    >
                      Sync Product Prices
                    </Button>

                    <NavLink
                      to={
                        isSubproduct
                          ? `/products/${mainProductId}/subproducts/${subproductId}/edit`
                          : `/products/${id}/edit`
                      }
                    >
                      <Button
                        icon={<EditOutlined />}
                        size="large"
                        style={{
                          backgroundColor: "rgb(29, 55, 100)",
                          color: "white",
                          marginRight: "0px",
                          marginLeft: "10px",
                          borderRadius: "4px",
                        }}
                        data-cy="test-product-edit-button"
                      >
                        Edit
                      </Button>
                    </NavLink>
                  </EntitledSection>
                )}

                {!isPhasedOut && (
                  <EntitledSection entitlement={entitlements.Products.delete}>
                    <Button
                      danger
                      size="large"
                      onClick={this.toggleDeleteConfirmationModal}
                      data-cy="test-product-delete-button"
                      style={{
                        marginLeft: "10px",
                        borderRadius: "4px",
                      }}
                    >
                      Phase out
                    </Button>
                  </EntitledSection>
                )}
              </div>

              <AntdCard
                title={product?.name || "Product"}
                style={{ marginBottom: "20px", marginTop: "20px" }}
              >
                <Descriptions>
                  <Descriptions.Item label={<b>Category</b>} key="Category">
                    <NavLink
                      to={`/product-categories/${product?.productCategory.id}`}
                    >
                      {product?.productCategory.name}
                    </NavLink>
                  </Descriptions.Item>
                  <Descriptions.Item label={<b>Prospect</b>} key="Prospect">
                    {product?.opportunityProspect?.name}
                  </Descriptions.Item>
                  <Descriptions.Item label={<b>Fund Code</b>} key="FundCode">
                    {product?.fundCode}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={<b>Asset Map Id</b>}
                    key="AssetMapId"
                  >
                    {product?.assetMapId}
                  </Descriptions.Item>
                  <Descriptions.Item label={<b>Tier</b>} key="Tier">
                    {product && this.showTier(product?.productTier.id)}
                  </Descriptions.Item>
                  {!isLoanOrFixedIncome && (
                    <Descriptions.Item
                      label={<b>Transaction Fields</b>}
                      key="TransactionFields"
                    >
                      {transactionFields?.join(" | ") ?? ""}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item
                    label={<b>Financial Institution</b>}
                    key="FinancialInstitution"
                  >
                    {product?.financialInstitution?.name}
                  </Descriptions.Item>
                  <Descriptions.Item label={<b>Van Code</b>} key="VanCode">
                    {product?.vanCode}
                  </Descriptions.Item>
                  <Descriptions.Item label={<b>Sync Price</b>} key="SyncPrice">
                    {capitalizeFirstLetter(product?.syncPrice.toString() || "")}
                  </Descriptions.Item>
                  {isLoanOrFixedIncome && (
                    <Descriptions.Item
                      label={<b>Interest Rate %</b>}
                      key="interest_per_annum"
                    >
                      {interestPerAnnum}
                    </Descriptions.Item>
                  )}
                  {isLoanOrFixedIncome && (
                    <Descriptions.Item
                      label={<b>Commission Fee</b>}
                      key="commission_fee"
                    >
                      GHS {commissionFee}
                    </Descriptions.Item>
                  )}
                  {isLoanOrFixedIncome && (
                    <Descriptions.Item
                      label={<b>Processing Fee %</b>}
                      key="processing_fee"
                    >
                      {processingFee}
                    </Descriptions.Item>
                  )}
                  {isLoanOrFixedIncome && (
                    <Descriptions.Item
                      label={<b>Insurance Fee</b>}
                      key="insurance_fee"
                    >
                      GHS {insuranceFee}
                    </Descriptions.Item>
                  )}
                  {isLoanOrFixedIncome && (
                    <Descriptions.Item
                      label={<b>Maximum Amount</b>}
                      key="max_amount"
                    >
                      GHS {maxAmount}
                    </Descriptions.Item>
                  )}
                  {isLoanOrFixedIncome && (
                    <Descriptions.Item
                      label={<b>Minimum Amount</b>}
                      key="min_amount"
                    >
                      GHS {minAmount}
                    </Descriptions.Item>
                  )}
                  {isLoanOrFixedIncome && (
                    <Descriptions.Item
                      label={<b>Maximum Tenor (months)</b>}
                      key="max_tenor"
                    >
                      {maxTenor}
                    </Descriptions.Item>
                  )}
                  {isLoanOrFixedIncome && (
                    <Descriptions.Item
                      label={<b>Max Balance % Accessible</b>}
                      key="max_balance_percentage_accessible"
                    >
                      {maxBalancePercentageAccessible}
                    </Descriptions.Item>
                  )}
                  {isLoanOrFixedIncome && (
                    <Descriptions.Item
                      label={<b>Lien Amount Multiplier</b>}
                      key="lien_amount_multiplier"
                    >
                      {lienAmountMultiplier}
                    </Descriptions.Item>
                  )}
                  {loanSecurityTypeValue && (
                    <Descriptions.Item
                      label={<b>Loan Security Type</b>}
                      key="loan_security_type"
                    >
                      {loanSecurityTypeLabel}
                    </Descriptions.Item>
                  )}
                  {product?.developerName && (
                    <Descriptions.Item
                      label={<b>Developer Name</b>}
                      key="developerName"
                    >
                      {product?.developerName}
                    </Descriptions.Item>
                  )}
                  {product?.developerContact && (
                    <Descriptions.Item
                      label={<b>Developer Contact</b>}
                      key="developerContact"
                    >
                      {product?.developerContact}
                    </Descriptions.Item>
                  )}
                  {product?.costOfProperty && (
                    <Descriptions.Item
                      label={<b>Cost of Property</b>}
                      key="costOfProperty"
                    >
                      {product?.costOfProperty}
                    </Descriptions.Item>
                  )}
                  {product?.productFundingOption && (
                    <Descriptions.Item
                      label={<b>Product Funding Option</b>}
                      key="productFundingOption"
                    >
                      {product?.productFundingOption}
                    </Descriptions.Item>
                  )}
                  {product?.mortgageDetails && (
                    <Descriptions.Item
                      label={<b>Mortgage Details</b>}
                      key="mortgageDetails"
                    >
                      {product?.mortgageDetails}
                    </Descriptions.Item>
                  )}
                  {product?.specificationsOfProperty && (
                    <Descriptions.Item
                      label={<b>Specifications of Property</b>}
                      key="specificationsOfProperty"
                    >
                      {product?.specificationsOfProperty}
                    </Descriptions.Item>
                  )}
                  {product?.accruedBenefitDistribution && (
                    <Descriptions.Item
                      label={<b>Accrued Benefit Distribution</b>}
                      key="accruedBenefitDistribution"
                    >
                      {product?.accruedBenefitDistribution}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </AntdCard>
            </div>

            <Collapse
              style={{ marginTop: "20px" }}
              defaultActiveKey={["priceHistory"]}
            >
              {subproducts && !isSubproduct && (
                <Collapse.Panel
                  header={
                    <div>
                      <span>Subproducts</span>
                      <Badge
                        count={subproducts?.length ?? 0}
                        style={tabPaneStyle}
                      />
                    </div>
                  }
                  key="subproducts"
                >
                  <div style={{ padding: "16px 0" }}>
                    {!isPhasedOut && (
                      <div
                        style={{
                          marginBottom: "20px",
                        }}
                      >
                        <Button
                          className="base-button"
                          size="large"
                          onClick={() =>
                            this.redirectToPage(`/products/${id}/subproducts/new`)
                          }
                          data-cy="test-new-product-button"
                          style={{
                            backgroundColor: "rgb(29, 55, 100)",
                            color: "white",
                            borderRadius: "6px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontWeight: 500,
                            boxShadow: "0 2px 6px rgba(29, 55, 100, 0.3)",
                            border: "none",
                          }}
                        >
                          <PlusOutlined />
                          Add Subproduct
                        </Button>
                      </div>
                    )}

                    {subproducts.length > 0 && (
                      <Table
                        scroll={{ x: 400 }}
                        columns={subproductsColumns(id)}
                        dataSource={subproducts}
                        style={{
                          border: "1px solid #f0f0f0",
                          borderRadius: "6px",
                        }}
                        pagination={{
                          showSizeChanger: true,
                          showQuickJumper: true,
                          showTotal: showTotalPagination,
                        }}
                      />
                    )}
                  </div>
                </Collapse.Panel>
              )}
              {product?.productTemplates && (
                <Collapse.Panel
                  header={
                    <div>
                      <span>Product Templates</span>
                      <Badge
                        count={product?.productTemplates?.length ?? 0}
                        style={tabPaneStyle}
                      />
                    </div>
                  }
                  key="productTemplates"
                >
                  <div style={{ padding: "16px 0" }}>
                    {!isPhasedOut && (
                      <Button
                        className="base-button"
                        size="large"
                        onClick={this.toggleAddProductTemplateModal}
                        style={{
                          backgroundColor: "rgb(29, 55, 100)",
                          color: "white",
                          borderRadius: "5px",
                          height: "35px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontWeight: 500,
                          boxShadow: "0 2px 6px rgba(29, 55, 100, 0.3)",
                          border: "none",
                        }}
                      >
                        <PlusOutlined />
                        Add Template To Product
                      </Button>
                    )}
                  </div>
                  <Table
                    bordered={true}
                    columns={templateColumns}
                    dataSource={injectKey<ProductTemplate>(
                      product?.productTemplates || [],
                      "id",
                    )}
                  />
                </Collapse.Panel>
              )}
              {product?.prices && (
                <Collapse.Panel
                  header={
                    <div>
                      <span>Price History</span>
                      <Badge
                        count={product?.prices?.length ?? 0}
                        style={tabPaneStyle}
                      />
                    </div>
                  }
                  key="priceHistory"
                >
                  <div style={{ padding: "24px 0 16px 0" }}>
                    {PriceHistoryTableFactory(
                      product?.prices,
                      getColumns(this.toggleShowClosePriceModal, isPhasedOut),
                    )}
                  </div>
                </Collapse.Panel>
              )}
            </Collapse>

            {showCreateEzHomeProductPriceModal &&
              ModalCreator({
                title: "Create EzHome Product Price",
                okType: "primary",
                okText: "Create",
                showModal: showCreateEzHomeProductPriceModal,
                onOkay: this.createEzHomeProductPrice,
                toggleModal: this.toggleCreateEzHomeProductPriceModal,
                inscription: CreateEzHomeProductPriceUI({
                  reference: this.createEzHomeProductPriceReference,
                  isCreatingEzHomeProductPrice,
                  dateChangeHandler: this.priceDateChangeHandler,
                }),
              })}
            {showAddProductTemplateModal &&
              ModalCreator({
                title: "Add Template To Product",
                okType: "primary",
                okText: "Add",
                showModal: showAddProductTemplateModal,
                isLoading: isAddingProductTemplate,
                onOkay: this.addProductTemplateToProduct,
                toggleModal: this.toggleAddProductTemplateModal,
                inscription: (
                  <BaseForm
                    layout="vertical"
                    reference={this.addProductTemplateReference}
                    fieldsToHide={[]}
                    fields={addProductTemplateFields}
                  />
                ),
              })}
          </Fragment>
        </Card>
      </Spin>
    );
  }
}
